import { useTheme } from '@emotion/react';
import { Button, Stack, TextField, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { remainingCredits } from '../../store/reducers/auth';
import { Logs, collectiveApiGetRequest, collectiveApiPostRequest } from '../../services';
import { ErrorText } from '../errorText';
import { generateValidationSchema } from './validations';
import { totalCreditsAndRemaining } from '../../services/creditsService';
import qs from 'qs';

export function InputField({
  queryParamsFlag = false,
  setloader = () => { },
  setJsonData = () => { },
  text = [],
  apiUrl = '',
  errorObject = null,
  setErrorObject = () => { },
  setStatus = () => { },
  setCurlUrl = () => { },
  setCurlBody = () => { },
  setCurlMethod = () => { },
  requestId = '',
  inputValues = {},
  onInputChange = () => { },
  buttonText = "Send",
  file = null,
  setVerificationResponse = null,
  linkedApis = [],
  jsonData = null,
}) {
  const theme = useTheme();
  const [curlRequestBody, setCurlRequestBody] = useState({});
  const dispatch = useDispatch();
  const { schema, maxLengthMap } = generateValidationSchema(text);
  const [hasFormChanged, setHasFormChanged] = useState(false);
  const [lastSubmittedValues, setLastSubmittedValues] = useState({});

  const form = useForm({
    resolver: yupResolver(schema),
    mode: 'onChange',
  });

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    getValues,
    formState: { errors, isValid },
  } = form;

  useEffect(() => {
    reset();
  }, [text, reset]);

  useEffect(() => {
    Object.entries(inputValues).forEach(([key, value]) => {
      if (value !== undefined) {
        setValue(key, value, { shouldValidate: true, shouldDirty: true });
      }
    });
  }, [inputValues, setValue]);

  const actionValueMap = {
    '/a1/search-tax-payer': 'TP',
    '/a1/view-track-returns': 'RETTRACK',
  };

  const actionValue = actionValueMap[apiUrl] || '';
  const isActionFieldDisabled = !!actionValueMap[apiUrl];

  useEffect(() => {
    if (actionValue) {
      setValue('action', actionValue, { shouldValidate: true, shouldDirty: true });
    }
  }, [apiUrl, actionValue, setValue]);

  useEffect(() => {
    if (requestId) {
      setValue('requestId', requestId, { shouldValidate: true, shouldDirty: true });
    }
  }, [requestId, setValue]);

  const handleApiRequest = async (data) => {

    try {
      setloader(true);
      setErrorObject(null);
      setJsonData(null);

      const filteredData = { ...data };
      if (linkedApis?.length > 0 && text?.length > 0) {
        delete filteredData.tran_id;
        delete filteredData.otp;
      }
      let requestData = filteredData;
      if (file) {
        const formData = new FormData();
        Object.keys(filteredData).forEach((key) => {
          if (filteredData[key] !== undefined && filteredData[key] !== null) {
            formData.append(key, filteredData[key]);
          }
        });
        if (file && typeof file === 'object' && !Array.isArray(file)) {
          Object.entries(file).forEach(([value, fileItem]) => {
            formData.append(`${value}`, fileItem);
          });
        } else if (file) {
          formData.append(`file`, file);
        }
        requestData = formData;
      }

      const requestHandler = queryParamsFlag ? collectiveApiGetRequest : collectiveApiPostRequest;
      const res = await requestHandler(requestData, apiUrl);

      setStatus(res?.status);
      setVerificationResponse(null)
      setLastSubmittedValues(filteredData);
      setHasFormChanged(false);
      if (res.status === 200) {
        setJsonData(res?.data);

        setTimeout(async () => {
          const remainingCredit = await totalCreditsAndRemaining();
          if (remainingCredit?.status === 200) {
            dispatch(
              remainingCredits({
                credits: remainingCredit?.data?.credit?.remainingCredit,
              }),
            );
            setloader(false);
          }
        }, 2000);
      }
    } catch (error) {
      setloader(false);
      const statusCode = error.response?.status || 500;
      const responseData = error.response?.data;
      const cleanErrorData =
        responseData && typeof responseData === 'object'
          ? { ...responseData, status: responseData.status || statusCode }
          : { error: responseData || error.message, status: statusCode };

      setErrorObject(cleanErrorData);
      setStatus(statusCode);
      setJsonData(cleanErrorData);
      Logs(error);
    }
  };



  const handleInput = (e, maxLength, fieldName) => {
    const inputValue = e.target.value.slice(0, maxLength);
    e.target.value = inputValue;

    onInputChange(fieldName, inputValue);
    setValue(fieldName, inputValue, { shouldValidate: true, shouldDirty: true });

    const changed = checkIfFormChanged();
    setHasFormChanged(changed);
    let updatedCurlRequestBody = {
      ...curlRequestBody,
      [fieldName]: inputValue || undefined,
    };

    const defaultFields = {};
    if (actionValue) {
      defaultFields['action'] = actionValue;
    }
    if (requestId) {
      defaultFields['requestId'] = requestId;
    }

    updatedCurlRequestBody = {
      ...updatedCurlRequestBody,
      ...defaultFields,
    };

    const filteredRequestBody = Object.fromEntries(
      Object.entries(updatedCurlRequestBody).filter(([key]) =>
        text.some((item) => item.value === key)
      )
    );

    const queryString = queryParamsFlag
      ? `?${qs.stringify(filteredRequestBody, { arrayFormat: 'brackets' })}`
      : '';
    const requestUrl = `${apiUrl}${queryString}`;
    const method = queryParamsFlag ? 'GET' : 'POST';
    setCurlRequestBody(filteredRequestBody);
    setCurlUrl(requestUrl);
    setCurlBody(queryParamsFlag ? null : filteredRequestBody);
    setCurlMethod(method);
  };

  const checkIfFormChanged = () => {
    const currentValues = getValues();

    for (const key in currentValues) {
      if ((currentValues[key] || '') !== (lastSubmittedValues[key] || '')) {
        return true;
      }
    }
    return false;
  };
  useEffect(() => {
    setLastSubmittedValues(inputValues);
  }, []);
  return (
    <Stack
      component="form"
      onSubmit={handleSubmit(handleApiRequest)}
      spacing={5}
      sx={{ mt: 2 }}
    >
      {text.length > 0 && (
        <Stack spacing={3}>
          {text.map(
            (item) =>
              item.label &&
              item.value && (
                <Stack key={item.id || item.value} spacing={1}>
                  <Typography
                    sx={{ color: theme.palette.grey.darken4 }}
                    variant="body1"
                  >
                    {item.label}
                  </Typography>
                  <TextField
                    sx={{ width: "100%" }}
                    onInput={(e) => {
                      handleInput(e, maxLengthMap[item.value], item.value);
                    }}
                    placeholder={`Enter ${item.label}`}
                    {...register(item.value)}
                    value={inputValues[item.value] || ""}
                    disabled={
                      (isActionFieldDisabled && item.value === "action") ||
                      (item.value === "requestId" && !!requestId) ||
                      (item.value === "tran_id" && !!inputValues.tran_id)
                    }
                    inputProps={{
                      maxLength: item.value === "otp" ? 6 : undefined,
                      inputMode: item.value === "otp" ? "numeric" : "text",
                      pattern: item.value === "otp" ? "\\d*" : undefined,
                    }}
                  />
                  {item.fieldInstruction && (
                    <Typography
                      variant="body1"
                      sx={{
                        color: theme.palette.text.secondary,
                        marginTop: "5px",
                      }}
                    >
                      {item.fieldInstruction}
                    </Typography>
                  )}
                  {!!errors[item.value] && (
                    <ErrorText>{errors[item.value].message}</ErrorText>
                  )}
                </Stack>
              )
          )}
        </Stack>
      )}

      <Button
        sx={{ width: "100%" }}
        variant="contained"
        type="submit"
        disabled={
          !isValid || (!hasFormChanged && jsonData && linkedApis?.length)
        }
      >
        {buttonText}
      </Button>
    </Stack>
  );
}

InputField.propTypes = {
  queryParamsFlag: PropTypes.bool,
  setloader: PropTypes.func,
  setJsonData: PropTypes.func,
  text: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string,
      value: PropTypes.string,
      fieldInstruction: PropTypes.string,
    }),
  ),
  apiUrl: PropTypes.string,
  errorObject: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  setErrorObject: PropTypes.func,
  setStatus: PropTypes.func,
  setCurlUrl: PropTypes.func,
  setCurlBody: PropTypes.func,
  setCurlMethod: PropTypes.func,
  requestId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  inputValues: PropTypes.object,
  onInputChange: PropTypes.func,
  buttonText: PropTypes.string,
  file: PropTypes.oneOfType([PropTypes.object, PropTypes.array, PropTypes.string]),
  setVerificationResponse: PropTypes.func,
  linkedApis: PropTypes.array,
  jsonData: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.array,
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
  ]),
};